import { toast } from 'react-toastify';

import { useDeleteQuery } from '@/queries/useDeleteQuery';
import { usePostQuery } from '@/queries/usePostQuery';
import useClientState from '@/state/useClientState';
import { toastSuccessOptions } from '@/utils/toastOptions';

type UseAccepteerConflictDialogProps = {
  handleOpenChange: (open: boolean) => void;
  onAccepted?: () => void;
};

type ConflictData = {
  conflictType: string | undefined;
  werklijnElementIds: string[];
};

type ConflictResponse = {
  resultaten?: {
    werklijnElementId: string;
    status: string;
    reden: string | null;
  }[];
};

export const useAccepteerConflictDialog = ({ handleOpenChange, onAccepted }: UseAccepteerConflictDialogProps) => {
  const { post } = usePostQuery<ConflictData, ConflictResponse>('/conflicten/accepteer');
  const { delete: deleteRequest } = useDeleteQuery<ConflictData, ConflictResponse>('/conflicten/accepteer');
  const selectedItems = useClientState((state) => state.selected.selectedItems);

  const isMultiSelect = selectedItems.length >= 2;
  const acceptedCount = selectedItems.filter((item) => item.geaccepteerdConflict === true).length;
  const selectionIsAllAccepted = selectedItems.length > 0 && acceptedCount === selectedItems.length;
  const selectionHasOneAcceptedConflict = isMultiSelect && !selectionIsAllAccepted && acceptedCount > 0;

  let conflictType: string | undefined;
  if (selectedItems[0]?.isManco) {
    conflictType = 'MANCO';
  } else if (selectedItems[0]?.isVerkeerdMat) {
    conflictType = 'VERKEERD_MATERIEEL';
  } else {
    conflictType = undefined;
  }

  const data: ConflictData = {
    conflictType,
    werklijnElementIds: selectedItems.map((item) => item.selectedId),
  };

  const handleAccepteerConflicten = async () => {
    if (conflictType) {
      const response = await post(data);
      const acceptedCount =
        (response as ConflictResponse).resultaten?.filter((item) => item.status === 'ACCEPTED').length || 0;
      toast.success(`${acceptedCount} conflict(en) geaccepteerd`, toastSuccessOptions);
      handleOpenChange(false);
      onAccepted?.();
    } else {
      console.log('No conflict type defined, cannot reject conflicts.');
    }
  };

  const handleAccepteerConflictNietMeer = async () => {
    if (conflictType) {
      const response = await deleteRequest(data);
      const acceptedCount =
        (response as ConflictResponse).resultaten?.filter((item) => item.status === 'DELETED').length || 0;
      toast.success(`${acceptedCount} conflict(en) niet meer geaccepteerd`, toastSuccessOptions);
      handleOpenChange(false);
      onAccepted?.();
    } else {
      console.log('No conflict type defined, cannot reject conflicts.');
    }
  };

  return {
    handleAccepteerConflicten,
    handleAccepteerConflictNietMeer,
    selectionHasOneAcceptedConflict,
    selectionIsAllAccepted,
  };
};
